import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["unrevealed", "revealed", "unrevealedText", "revealedText", "title", "subhead", "tapOverlay"]

  isRevealed = false

  connect() {
    // Find the year-in-review controller to communicate with it
    this.yearInReviewController = this.findYearInReviewController()

    // Listen for tap/click on the tap overlay (which sits above navigation zones)
    this.boundHandleTap = this.handleTap.bind(this)
    if (this.hasTapOverlayTarget) {
      this.tapOverlayTarget.addEventListener("click", this.boundHandleTap)
      this.tapOverlayTarget.addEventListener("touchend", this.boundHandleTap)
    }

    // Note: We do NOT call setRequiresInteraction here because all stories are
    // pre-rendered in the DOM. The year_in_review_controller checks the
    // data-requires-interaction attribute when transitioning to a story instead.
  }

  disconnect() {
    if (this.hasTapOverlayTarget) {
      this.tapOverlayTarget.removeEventListener("click", this.boundHandleTap)
      this.tapOverlayTarget.removeEventListener("touchend", this.boundHandleTap)
    }
  }

  findYearInReviewController() {
    const yearInReviewElement = document.querySelector("[data-controller*='year-in-review']")
    if (yearInReviewElement) {
      const application = this.application
      return application.getControllerForElementAndIdentifier(yearInReviewElement, "year-in-review")
    }
    return null
  }

  handleTap(event) {
    // Ignore if already revealed
    if (this.isRevealed) return

    // Don't interfere with interactive elements
    if (event.target.closest("button, a")) return

    // Prevent the year-in-review controller from handling this tap for navigation
    event.stopPropagation()

    this.reveal()
  }

  reveal() {
    if (this.isRevealed) return
    this.isRevealed = true

    // Hide tap overlay immediately so it doesn't block further interactions
    if (this.hasTapOverlayTarget) {
      this.tapOverlayTarget.style.display = "none"
    }

    // Animate unrevealed card out
    if (this.hasUnrevealedTarget) {
      this.unrevealedTarget.classList.add("is-exiting")
    }

    // Animate unrevealed text out
    if (this.hasUnrevealedTextTarget) {
      this.unrevealedTextTarget.classList.add("is-exiting")
    }

    // After exit animation, show revealed state
    setTimeout(() => {
      // Hide unrevealed elements
      if (this.hasUnrevealedTarget) {
        this.unrevealedTarget.style.display = "none"
      }
      if (this.hasUnrevealedTextTarget) {
        this.unrevealedTextTarget.style.display = "none"
      }

      // Show revealed card
      if (this.hasRevealedTarget) {
        this.revealedTarget.classList.remove("pointer-events-none")
        this.revealedTarget.style.opacity = "1"
        this.revealedTarget.classList.add("is-revealing")
      }

      // Show revealed text
      if (this.hasRevealedTextTarget) {
        this.revealedTextTarget.style.opacity = "1"
        this.revealedTextTarget.classList.add("is-revealing")
      }

      // Notify year-in-review controller that interaction is complete
      if (this.yearInReviewController) {
        this.yearInReviewController.setRequiresInteraction(false)
        this.yearInReviewController.onArchetypeRevealed()
      }
    }, 400) // Match the exit animation duration
  }

  // Called when revisiting this story (already revealed)
  showRevealed() {
    this.isRevealed = true

    // Hide tap overlay
    if (this.hasTapOverlayTarget) {
      this.tapOverlayTarget.style.display = "none"
    }

    // Hide unrevealed elements
    if (this.hasUnrevealedTarget) {
      this.unrevealedTarget.style.display = "none"
    }
    if (this.hasUnrevealedTextTarget) {
      this.unrevealedTextTarget.style.display = "none"
    }

    // Show revealed card (skip animation)
    if (this.hasRevealedTarget) {
      this.revealedTarget.classList.remove("pointer-events-none")
      this.revealedTarget.style.opacity = "1"
    }

    // Show revealed text with final styles (skip animation)
    if (this.hasRevealedTextTarget) {
      this.revealedTextTarget.style.opacity = "1"
    }

    // Set title and subhead to visible
    if (this.hasTitleTarget) {
      this.titleTarget.style.opacity = "1"
      this.titleTarget.style.transform = "translateY(0)"
    }
    if (this.hasSubheadTarget) {
      this.subheadTarget.style.opacity = "1"
      this.subheadTarget.style.transform = "translateY(0)"
    }
  }
}
